import type { ApiRequestFailure } from './apiRequestFailure';
import { parseApiRequestFailureBody } from './apiRequestFailureBody';
import { impossible } from 'common/types/impossible';

type Options = {
  // Message to show for an UNEXPECTED_ERROR that carries no usable server
  // message (neither a JSON `message` field nor a JSON-string body). Domains
  // pass their own copy here (e.g. the anomaly modal's "failed to submit
  // report"); defaults to the translated generic error copy.
  //
  // Deliberately not consulted for NO_INTERNET_CONNECTION: that state has its
  // own translated string, which names the actual problem more precisely than
  // a domain's "could not do X" copy can.
  fallbackMessage?: string;
};

// Endpoints predating the v3 shape answer with a bare `{message}` or `{error}`
// -- `api/v1/event_requests` on a 401, the commission commands on a 422. Read
// them directly: the v3 schema would report a mismatch to Honeybadger for a
// body working as designed, and drop the message besides.
//
// Bodies carrying `status` are v3 and go to the schema, whose optional
// `message` falls through to the caller's own copy when absent.
function bareErrorMessage(responseJson: unknown): string | null {
  if (
    typeof responseJson !== 'object' ||
    responseJson === null ||
    Array.isArray(responseJson) ||
    'status' in responseJson
  ) {
    return null;
  }
  const body = responseJson as { message?: unknown; error?: unknown };
  const copy = body.message ?? body.error;
  return typeof copy === 'string' && copy.length > 0 ? copy : null;
}

export default function apiRequestFailureToMessage(
  apiError: ApiRequestFailure,
  options: Options = {}
): string {
  const { errorState, responseJson } = apiError;
  const fallbackMessage =
    options.fallbackMessage ?? __('error.something_went_wrong');

  switch (errorState) {
    case 'NO_INTERNET_CONNECTION':
      return __('error.no_internet_connection');
    case 'UNAUTHORIZED':
      return __('unauthorized');
    case 'UNEXPECTED_ERROR': {
      // A bare JSON-string body is a message the server rendered for the
      // user -- the message controllers respond with e.g.
      // `render json: form.errors.full_messages.to_sentence, status: 422`.
      if (typeof responseJson === 'string' && responseJson.length > 0) {
        return responseJson;
      }
      const bare = bareErrorMessage(responseJson);
      if (bare) {
        return bare;
      }
      const errorBody = parseApiRequestFailureBody(responseJson);
      if (errorBody?.message) {
        return errorBody.message;
      }
      // Anything else -- an HTML error page, a JSON shape without a usable
      // message, an empty body -- is not user-presentable copy.
      return fallbackMessage;
    }
    default:
      return impossible(errorState);
  }
}
